import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';
import { Label } from '../../ui/label';
import { normalizeDocumentTypeCode } from '../../../lib/utils';

interface SelectDocumentTypeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (type: string, subType?: string) => void;
  suggestedType?: string;
  onApprove?: () => void;
}

const mainTypes = [
  { id: 'INVOICE', label: 'Factures (Invoices)' },
  { id: 'BANK_STATEMENT', label: 'Relevés bancaires (Bank Statements)' },
  { id: 'TAX_FORM', label: 'Documents Fiscaux' },
  {
    id: 'LAW_TEXT_OR_PRESS_RELEASE',
    label: 'Textes de Loi ou Communiqués Fiscaux',
  },
];

export const SelectDocumentTypeDialog: React.FC<SelectDocumentTypeDialogProps> = ({
  isOpen,
  onClose,
  onSelect,
  suggestedType,
  onApprove,
}) => {
  const [selectedMainType, setSelectedMainType] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'confirm' | 'edit'>('edit');
  const { t } = useTranslation();

  React.useEffect(() => {
    if (isOpen) {
      if (suggestedType) {
        setViewMode('confirm');
        const suggested = mainTypes.find((t) => t.id.toLowerCase() === suggestedType.toLowerCase());
        setSelectedMainType(suggested ? suggested.id : null);
      } else {
        setViewMode('edit');
        setSelectedMainType(null);
      }
    }
  }, [isOpen, suggestedType]);

  const handleSelect = () => {
    if (selectedMainType) {
      onSelect(selectedMainType);
      resetAndClose();
    }
  };

  const handleApprove = () => {
    if (onApprove) {
      onApprove();
    }
    resetAndClose();
  };

  const resetAndClose = () => {
    setSelectedMainType(null);
    onClose();
  };

  const renderConfirmationView = () => (
    <>
      <DialogHeader>
        <DialogTitle>{t('dialog.confirmCategory')}</DialogTitle>
        <DialogDescription>{t('dialog.aiDetected')}</DialogDescription>
      </DialogHeader>
      <div className="py-8 text-center flex flex-col items-center justify-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <svg
            className="w-10 h-10 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 13l4 4L19 7"
            ></path>
          </svg>
        </div>
        <p className="text-sm text-gray-500">Document détecté comme :</p>
        <p className="text-2xl font-bold text-blue-600 mt-1">
          {suggestedType ? t(`docType.${normalizeDocumentTypeCode(suggestedType)}`) : ''}
        </p>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => setViewMode('edit')}>
          {t('actions.edit')}
        </Button>
        <Button onClick={handleApprove}>{t('actions.confirm')}</Button>
      </DialogFooter>
    </>
  );

  const renderEditView = () => (
    <>
      <DialogHeader>
        <DialogTitle>{t('dialog.chooseDocType')}</DialogTitle>
        <DialogDescription>{t('dialog.chooseMainCategory')}</DialogDescription>
      </DialogHeader>
      <RadioGroup
        value={selectedMainType || ''}
        onValueChange={setSelectedMainType}
        className="py-4 grid gap-4"
      >
        {mainTypes.map((type) => (
          <div key={type.id} className="flex items-center space-x-2">
            <RadioGroupItem value={type.id} id={type.id} />
            <Label htmlFor={type.id}>{type.label}</Label>
          </div>
        ))}
      </RadioGroup>
      <DialogFooter>
        <Button variant="outline" onClick={resetAndClose}>
          {t('actions.cancel')}
        </Button>
        <Button onClick={handleSelect} disabled={!selectedMainType}>
          {t('actions.validate')}
        </Button>
      </DialogFooter>
    </>
  );

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent className="sm:max-w-[425px]">
        {viewMode === 'confirm' && suggestedType ? renderConfirmationView() : renderEditView()}
      </DialogContent>
    </Dialog>
  );
};
